import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useLayoutEffect, useRef, useState } from 'react'

import TabBar, { type TabBarOption } from '@/components/ui/TabBar'
import { cn } from '@/lib/utils'

/** How far an arrow scrolls: most of the strip, so the tab cut off at its edge stays in sight. */
const ARROW_STEP = 0.7

/** The fade on whichever side still has tabs past it, as the section strip draws it. */
const FADE = {
  none: null,
  end: '[mask-image:linear-gradient(to_right,black_calc(100%-var(--strip-fade-w)),transparent)]',
  start: '[mask-image:linear-gradient(to_left,black_calc(100%-var(--strip-fade-w)),transparent)]',
  both: '[mask-image:linear-gradient(to_right,transparent,black_var(--strip-fade-w),black_calc(100%-var(--strip-fade-w)),transparent)]',
} as const

interface Overflow {
  readonly before: boolean
  readonly after: boolean
}

interface ColumnSetTabsProps {
  options: readonly TabBarOption[]
  value: string
  onChange: (value: string) => void
  className?: string
}

/**
 * The table's column sets (the mock's `dxe`): the app's underline tabs on one line that scrolls
 * when the sets outrun the column, with a fade and an arrow on each side that has more, as the
 * section strip does (`SectionTabs`). The arrows step 70% of the strip and stay out of the tab
 * order: a keyboard walks the tabs, and TabBar scrolls the chosen one into view.
 */
export default function ColumnSetTabs({ options, value, onChange, className }: ColumnSetTabsProps) {
  const rootRef = useRef<HTMLDivElement>(null)
  const [overflow, setOverflow] = useState<Overflow>({ before: false, after: false })

  useLayoutEffect(() => {
    const strip = rootRef.current?.querySelector<HTMLElement>('[role="tablist"]')
    if (strip === null || strip === undefined) return
    const measure = () => {
      // 1px of slack: a fractional width can leave a sub-pixel the strip never scrolls.
      const before = strip.scrollLeft > 1
      const after = strip.scrollWidth - strip.clientWidth - strip.scrollLeft > 1
      setOverflow((previous) =>
        previous.before === before && previous.after === after ? previous : { before, after },
      )
    }
    measure()
    strip.addEventListener('scroll', measure, { passive: true })
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure)
    observer?.observe(strip)
    return () => {
      strip.removeEventListener('scroll', measure)
      observer?.disconnect()
    }
  }, [options])

  const step = (direction: -1 | 1) => {
    const strip = rootRef.current?.querySelector<HTMLElement>('[role="tablist"]')
    strip?.scrollBy({ left: direction * ARROW_STEP * strip.clientWidth })
  }

  const fade =
    overflow.before && overflow.after
      ? FADE.both
      : overflow.before
        ? FADE.start
        : overflow.after
          ? FADE.end
          : FADE.none

  return (
    <div ref={rootRef} className={cn('relative min-w-0', className)}>
      <TabBar
        options={options}
        value={value}
        onChange={onChange}
        ariaLabel="Column sets"
        stripClassName={cn(
          'scroll-smooth motion-reduce:scroll-auto lg:overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
          fade,
        )}
      />
      {overflow.before ? <SetArrow direction={-1} onStep={step} /> : null}
      {overflow.after ? <SetArrow direction={1} onStep={step} /> : null}
    </div>
  )
}

/** An overflow arrow over the strip's edge, on the page's own colour, gone on a touch screen. */
function SetArrow({
  direction,
  onStep,
}: {
  direction: -1 | 1
  onStep: (direction: -1 | 1) => void
}) {
  const Chevron = direction === -1 ? ChevronLeft : ChevronRight
  return (
    <button
      type="button"
      tabIndex={-1}
      aria-label={direction === -1 ? 'Show earlier column sets' : 'Show more column sets'}
      onClick={() => {
        onStep(direction)
      }}
      className={cn(
        'absolute top-0 flex h-9 w-8 items-center justify-center bg-background text-muted-foreground',
        'transition-colors hover:text-foreground pointer-coarse:hidden',
        direction === -1 ? 'left-0' : 'right-0',
      )}
    >
      <Chevron className="size-4" aria-hidden="true" />
    </button>
  )
}
